// src/js/core/generateDialogueUI.js
import { supabaseClient } from "./supabaseClient.js";
import { updateVoiceSelectVisibility } from "./voiceConfig.js";
import {
  getCurrentLanguage,
  getCurrentLanguageWords,
  updateCurrentLanguageWords,
  canAddMoreWords,
  getWordsForPrompt,
} from "./wordManager.js";
import { normalizeWord, splitIntoWords, isOnlyPunctuation } from "./wordNormalizer.js";
import { getTranslation } from "./translateWords.js";
import { showTooltip } from "./tooltip.js";
import { updateUnknownWordsList } from "./wordListUI.js";
import { getRandomTopic } from "./topics.js";

const DIALOGUE_USAGE_INFO_KEY = "dialogue_usage_info";

// Simple check if a string is a single emoji (potentially with punctuation)
const isEmoji = (text) => /^\p{Extended_Pictographic}\u{FE0F}?[\s,\.!?]*$/u.test(text);

// Strip emojis for TTS payload — TTS providers can't pronounce them and bill for the input.
function stripEmojisForTts(text) {
  return text
    .replace(/\p{Extended_Pictographic}\u{FE0F}?/gu, "")
    .replace(/\s+([.,!?;:])/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

function updateUIForLimit() {
  document.body.classList.add("limit-reached");
}

function initializeWordClickHandler(outputDiv) {
  outputDiv.addEventListener("click", (e) => {
    if (e.target.matches(".word")) {
      const word = normalizeWord(e.target.dataset.word, getCurrentLanguage());
      const currentWords = getCurrentLanguageWords();

      // Only handle marking words as unknown, not removing them
      if (!currentWords.has(word)) {
        if (canAddMoreWords()) {
          currentWords.add(word);
          updateCurrentLanguageWords(currentWords);

          document.querySelectorAll(`.word[data-word="${word}"]`).forEach((el) => {
            el.classList.add("unknown");
          });

          updateUnknownWordsList();

          // Show translation immediately after marking as unknown
          getTranslation(word, getCurrentLanguage())
            .then((translation) => {
              if (translation) {
                showTooltip(word, translation, e.target);
              }
            })
            .catch((error) => {
              console.error("Translation error:", error);
            });
        } else {
          alert(
            "100 word limit reached for this language! Use the quiz button to review saved words and remove some. Don't worry about not knowing them perfectly."
          );
        }
      }
    }
  });
}

// Selects a random subset of items from an array.
function selectRandomFromArray(array, count) {
  if (!array || !Array.isArray(array) || array.length === 0 || count <= 0) {
    return [];
  }
  const actualCount = Math.min(count, array.length);
  // Shuffle a copy and slice
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]; // Swap elements
  }
  return shuffled.slice(0, actualCount);
}

// Prompt construction (Now async due to dynamic imports for cognates)
async function buildPrompt(language, level) {
  // Constants for word limits
  const MAX_WORDS_WITH_TOPIC = 3;
  const MAX_WORDS_WITHOUT_TOPIC = 5;
  const MAX_COGNATES = 3;

  // Check for user-provided topic first
  const topicInput = document.getElementById("topic-input");
  let userTopic = topicInput?.value?.trim() || "";
  let randomTopic = "";

  // Only use random topic if no user topic AND less than 3 unknown words
  if (!userTopic && getCurrentLanguageWords().size < 3) {
    randomTopic = getRandomTopic();
  }

  // Determine max words based on whether we have a topic
  const maxWords = userTopic || randomTopic ? MAX_WORDS_WITH_TOPIC : MAX_WORDS_WITHOUT_TOPIC;

  // Get words to include with dynamic limit
  let wordsToInclude = getWordsForPrompt(maxWords);

  // Handle cognates for beginner level
  if (level === "beginner") {
    try {
      const cognateModule = await import(`../cognates/cognates.${language}.js`);
      const languageCognates = cognateModule.default;

      if (Array.isArray(languageCognates) && languageCognates.length > 0) {
        const cognatesToSuggest = selectRandomFromArray(languageCognates, MAX_COGNATES);
        if (cognatesToSuggest.length > 0) {
          const combinedWordsSet = new Set([...wordsToInclude, ...cognatesToSuggest]);
          wordsToInclude = Array.from(combinedWordsSet);
        }
      }
    } catch (error) {
      console.warn(`Could not load cognates for language "${language}" from cognates folder:`, error);
    }
  }

  // Construct the prompt parts
  const wordPrompt =
    wordsToInclude.length > 0 ? `Naturally incorporate all or most of these words: ${wordsToInclude.join(", ")}. ` : "";

  // Different prompt structure for user topics vs random topics
  const topicPrompt = userTopic ? `The theme is ${userTopic}` : randomTopic ? `They are ${randomTopic}` : "";

  // Select appropriate prompt builder based on level
  if (level === "beginner") {
    return buildBeginnerPrompt(language, topicPrompt, wordPrompt);
  } else {
    return buildStandardPrompt(language, level, topicPrompt, wordPrompt);
  }
}

function buildBeginnerPrompt(language, topicPrompt, wordPrompt) {
  const finalPrompt = `Create a beginner level ${language} dialogue between a man and a woman.
    ${topicPrompt ? `${topicPrompt}. ` : ""}${wordPrompt}
    Simple vocabulary.
    Use structured repetition (repeat key phrases with slight variations).
    Emojis after most (not all) words.
    Put a space before and after each emoji.
    Place emojis after (not before) the word it represents 
    and before any punctuation.
    Example: "I 🧍 see 👀 a white ⬜ cat 🐱 walking 🚶 in the street 🛣️ ." 
    Notice there are emojis after most words and they have spaces around them.
    Return JSON as specified:
    {
      "dialogue": [
        {
           "turnText": "Hello 👋 ! How was your weekend 📅 ? I went 🚶 to the park 🌳 with my dog 🐕 .",
           "chunks": [
            {"chunk": "I went 🚶 to the park 🌳 ",
              "translation": "Literal English translation without emojis"},
            {"chunk": "with my dog 🐕 .",
              "translation": "Literal English translation without emojis"},
            {"chunk": "There were a lot of people",
              "translation": "Literal English translation without emojis"},
            {"chunk": "at the park 🌳 this past weekend 📅 .",
              "translation": "Literal English translation without emojis"},
          ]
        },
       { /* ... up to 4 total turns */ }
      ]
    }
    Dialogue: 4 turns, male speaks first. Each turn: 2-3 sentences.
    Chunk turnText: 2-5 words each (MAX 5). Sentences may break.
    Chunk count varies naturally per turn.
    Chunking examples:
       "The colors 🎨 at the party 🎉 " + "were very vibrant."
       "It was well planned 📝 " + "and a lot of fun 🎉 ."
       Notice how each chunk ("were very vibrant.") is short. 
    ${
      ["chinese", "japanese"].includes(language)
        ? "Add spaces between words even though this is not standard formatting for the language."
        : ""
    }
    Return JSON only.`;

  console.log("Complete beginner prompt:", finalPrompt);
  return finalPrompt;
}

function buildStandardPrompt(language, level, topicPrompt, wordPrompt) {
  const finalPrompt = `Generate a dialogue in ${language} between a man and a woman,
    using language suitable for a ${level} level learner.
    ${topicPrompt ? `${topicPrompt}. ` : ""}${wordPrompt}
    Return the response in this JSON format:
    {
      "dialogue": [
        {
          "turnText": "first paragraph text"
        },
        {
          "turnText": "second paragraph text"
        }
        // ... and so on for 6 total turns
      ]
    }
    The dialogue should be exactly 6 turns, with the male speaking first.
    Each speaker's text should be 2-3 sentences.
    ${
      ["chinese", "japanese"].includes(language)
        ? "Add spaces between words even though this is not standard formatting for the language."
        : ""
    }
    Only return the JSON, nothing else.`;

  console.log("Complete standard prompt:", finalPrompt);
  return finalPrompt;
}

function cleanJsonResponse(jsonString) {
  // Remove markdown code block markers if they exist
  let cleaned = jsonString.replace(/^```json\n|\n```$/g, "").trim();

  // Fix incorrectly quoted text within JSON strings
  cleaned = cleaned.replace(/" , "/g, " ");
  cleaned = cleaned.replace(/" ,"/g, " ");
  cleaned = cleaned.replace(/","/g, " ");

  // Log the string we're trying to parse
  console.log("Attempting to parse JSON string:", cleaned);

  try {
    return JSON.parse(cleaned);
  } catch (error) {
    console.error("Error parsing cleaned JSON:", error);
    // Log exact position where parse failed
    console.log("Error occurred at position:", error.message.match(/position (\d+)/)?.[1]);
    // Log the substring around the error
    const pos = parseInt(error.message.match(/position (\d+)/)?.[1] || "0");
    console.log("Substring around error:", cleaned.slice(Math.max(0, pos - 20), pos + 20));
    throw error;
  }
}

function renderDialogue(outputDiv, generatedText) {
  const currentWords = getCurrentLanguageWords();
  const currentLevel = document.getElementById("level-select").value;

  // Get current TTS usage info for button text
  let buttonText = "Play audio"; // Default text when no usage info
  let isDisabled = false;
  const usageInfoStr = localStorage.getItem("tts_usage_info");

  if (usageInfoStr) {
    const usageInfo = JSON.parse(usageInfoStr);
    const ttsLimit = usageInfo.limit || 2; // Fallback to 2 if limit not provided
    const isAuthenticated = usageInfo.is_authenticated || false;

    // Check if the stored data is from the current day
    const now = new Date();
    const lastUpdated = new Date(usageInfo.last_updated);

    const isSameDay =
      now.getUTCFullYear() === lastUpdated.getUTCFullYear() &&
      now.getUTCMonth() === lastUpdated.getUTCMonth() &&
      now.getUTCDate() === lastUpdated.getUTCDate();

    if (isSameDay) {
      const remainingUses = Math.max(0, ttsLimit - usageInfo.daily_count);
      if (remainingUses === 0) {
        buttonText = isAuthenticated ? "0 left today" : "Sign in free for more audio";
      } else {
        buttonText = isAuthenticated ? `${remainingUses} left today` : "Play audio";
      }
      isDisabled = remainingUses === 0;
    }
  }

  try {
    const dialogueData = cleanJsonResponse(generatedText);

    outputDiv.innerHTML = dialogueData.dialogue
      .map((turn, index) => {
        const isSpeaker1 = index % 2 === 0; // Even indices are speaker1/male

        // Different content based on level
        const contentHtml =
          currentLevel === "beginner"
            ? turn.chunks
                .map(
                  (chunk) => `
          <p class="paragraph-chunk">
            ${splitIntoWords(chunk.chunk, getCurrentLanguage())
              .map((word) => {
                if (isOnlyPunctuation(word)) {
                  return word;
                }
                const cleanWord = normalizeWord(word, getCurrentLanguage());
                const isEmojiWord = isEmoji(word);
                const classes = ["word", currentWords.has(cleanWord) ? "unknown" : "", isEmojiWord ? "emoji" : ""]
                  .filter(Boolean)
                  .join(" ");
                return `<span class="${classes}" data-word="${cleanWord}">${word}</span>`;
              })
              .join(" ")}
          </p>
          <p class="chunk-translation">${chunk.translation}</p>
        `
                )
                .join("")
            : `<p class="paragraph-text">
      ${splitIntoWords(turn.turnText, getCurrentLanguage())
        .map((word) => {
          if (isOnlyPunctuation(word)) {
            return word;
          }
          const cleanWord = normalizeWord(word, getCurrentLanguage());
          return `<span class="word ${currentWords.has(cleanWord) ? "unknown" : ""}" 
            data-word="${cleanWord}">${word}</span>`;
        })
        .join(" ")}
     </p>`;

        return `
     <div class="paragraph-container ${isSpeaker1 ? "speaker1" : "speaker2"}" 
          data-tts-text="${
            currentLevel === "beginner"
              ? turn.chunks.map((chunk) => stripEmojisForTts(chunk.chunk)).join(" &lt;break time=&quot;0.4s&quot; /&gt; ")
              : turn.turnText
          }"
     >
      ${contentHtml}
       <div class="paragraph-controls" style="display:flex;justify-content:space-between;">
         <button class="audio-btn ${isDisabled ? "audio-btn-disabled" : ""}" 
                 ${isDisabled ? "disabled" : ""}
                 aria-label="Play audio">${buttonText}</button>
         ${
           currentLevel !== "beginner" ? `<button class="translate-btn" aria-label="Translate paragraph"></button>` : ""
         }
       </div>
     </div>
   `;
      })
      .join("");
  } catch (error) {
    console.error("Error parsing dialogue JSON:", error);
    outputDiv.innerHTML = '<p class="error">Error displaying the dialogue. Please try again.</p>';
  }

  updateVoiceSelectVisibility();
}

export function initializeDialogueGeneration() {
  const generateButtons = document.querySelectorAll(".generate-dialogue-btn");
  const outputDiv = document.getElementById("output");
  const topicInput = document.getElementById("topic-input");

  // Initialize word click handling
  initializeWordClickHandler(outputDiv);

  // Initialize generate buttons
  generateButtons.forEach((generateBtn) => {
    const buttonOriginalContent = generateBtn.innerHTML;

    generateBtn.addEventListener("click", async () => {
      try {
        // Clear TTS cache before generating new dialogue
        window.clearTTSCache?.();
        // Add loading class to body
        document.body.classList.add("loading-dialogue");
        // Disable all generate buttons while processing
        generateButtons.forEach((btn) => {
          btn.innerHTML = '<div class="spin"></div>';
          btn.disabled = true;
        });

        outputDiv.textContent = "";

        const language = getCurrentLanguage();
        const level = document.getElementById("level-select").value;
        // *** Await the result of buildPrompt as it's now async ***
        const prompt = await buildPrompt(language, level);

        // Get auth session for Supabase
        const {
          data: { session },
        } = await supabaseClient.auth.getSession();

        // Make request to Netlify function
        const response = await fetch("/.netlify/functions/generateDialogue", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({
            messages: [{ role: "user", content: prompt }],
            temperature: 0.8,
          }),
        });

        const data = await response.json();

        // Store usage info in localStorage
        if (data.usage_info) {
          localStorage.setItem(
            DIALOGUE_USAGE_INFO_KEY,
            JSON.stringify({
              daily_count: data.usage_info.daily_count,
              limit_reached: data.usage_info.limit_reached,
              limit: data.usage_info.limit,
              is_authenticated: data.usage_info.is_authenticated,
              is_premium: data.usage_info.is_premium,
              user_type: data.usage_info.user_type,
              last_updated: new Date().toISOString(),
            })
          );
        }

        // Handle limit reached error
        if (response.status === 429) {
          updateUIForLimit();
          outputDiv.textContent = "";
          return;
        }

        // Handle other errors
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        renderDialogue(outputDiv, data.choices[0].message.content);
        // Clear the topic input after successful generation
        if (topicInput) {
          topicInput.value = "";
        }
      } catch (error) {
        console.error("Error:", error);
        outputDiv.textContent = "An error occurred while generating the text.";
      } finally {
        // Remove loading class from body
        document.body.classList.remove("loading-dialogue");
        // Restore all buttons
        generateButtons.forEach((btn) => {
          btn.innerHTML = buttonOriginalContent;
          // Only enable the buttons if the limit hasn't been reached
          if (!document.body.classList.contains("limit-reached")) {
            btn.disabled = false;
          }
        });
      }
    });
  });
}
